import { Box } from "@mui/material";
import { type GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";

const STANDARD_TEXT_FIELD_PROPS = { variant: "standard" as const, inputSx: { px: 1 } };
// Cap the options list so a department with many jobs / role strings scrolls instead of
// overflowing the viewport (the PBJ-job dropdown can hold the full CMS job registry).
const OPTIONS_MAX_HEIGHT_PX = 280;
const SCROLL_SLOT_PROPS = { listbox: { sx: { maxHeight: OPTIONS_MAX_HEIGHT_PX } } } as const;

/**
 * Grid edit cell that picks a PBJ job by id. Used by both the "PBJ Job" (labelled by description)
 * and "Pay Title Code" (labelled by code) columns — each column's valueSetter funnels the chosen
 * id into `pbjJobId`, so editing either is the same underlying change. The listbox is height-capped
 * and scrollable.
 */
export const JobSelectEditCell: FC<GridRenderEditCellParams & { options: SystemAutoCompleteOption<string>[] }> = ({ id, field, value, options }) => {
	const apiRef = useGridApiContext();
	const selected = options.find((o) => o.value === value) ?? null;
	return (
		<SystemAutoComplete<string>
			autoFocus
			fullWidth
			options={options}
			value={selected}
			onChange={(_e, opt) => {
				apiRef.current.setEditCellValue({ id, field, value: opt?.value ?? "" });
				apiRef.current.stopCellEditMode({ id, field });
			}}
			textFieldProps={STANDARD_TEXT_FIELD_PROPS}
			slotProps={SCROLL_SLOT_PROPS}
			dataTestId={`pbj-staffing-edit-${field}-${id}`}
		/>
	);
};

/**
 * Fixed option list for the manual Pay Type Code override editor (grid edit cells + the bulk-edit
 * dialog). The leading "" option clears the override, reverting the row to its derived code.
 */
export const usePayTypeCodeOptions = (): SystemAutoCompleteOption<string>[] => {
	const { t } = useTranslation();
	return useMemo(
		() => [
			{ value: "", label: t("pbj_model.staffing.pay_type.derived") },
			{ value: "1", label: t("pbj_model.staffing.pay_type.exempt") },
			{ value: "2", label: t("pbj_model.staffing.pay_type.non_exempt") },
			{ value: "3", label: t("pbj_model.staffing.pay_type.contract") },
		],
		[t],
	);
};

/**
 * Subtle "manually set" indicator for a Pay Type Code cell whose value is a manual override
 * (payTypeCodeOverride != null): a small primary dot with an explanatory tooltip. Rendered via
 * EditableHoverCell's adornment slot (editable rows) or inline (aggregated rows).
 */
export const PayTypeOverrideBadge: FC = () => {
	const { t } = useTranslation();
	return (
		<InHouseTooltip title={t("pbj_model.staffing.pay_type.overridden_tooltip")} placement="top" arrow>
			{/* Padded hit area so the tiny dot is actually hoverable. */}
			<Box
				component="span"
				role="img"
				aria-label={t("pbj_model.staffing.pay_type.overridden")}
				data-testid="pbj-staffing-pay-type-override-badge"
				sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", p: 0.5, flexShrink: 0 }}
			>
				<Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "primary.main", display: "block" }} />
			</Box>
		</InHouseTooltip>
	);
};

/**
 * Free-text-or-select grid edit cell for the raw Job Description (`rawJob`): analysts can type any
 * value, or pick from the department's known role strings (the Jobs-tab mapping). freeSolo keeps the
 * field open-ended since raw_job mirrors whatever the punch feed emitted.
 */
export const RoleStringEditCell: FC<GridRenderEditCellParams & { options: string[] }> = ({ id, field, value, options }) => {
	const apiRef = useGridApiContext();
	const opts = useMemo<SystemAutoCompleteOption<string>[]>(() => options.map((o) => ({ label: o, value: o })), [options]);
	const selected = typeof value === "string" && value ? { label: value, value } : null;
	return (
		<SystemAutoComplete<string>
			freeSolo
			autoFocus
			fullWidth
			options={opts}
			value={selected}
			onChange={(_e, opt) => {
				apiRef.current.setEditCellValue({ id, field, value: opt?.value || null });
				apiRef.current.stopCellEditMode({ id, field });
			}}
			textFieldProps={STANDARD_TEXT_FIELD_PROPS}
			slotProps={SCROLL_SLOT_PROPS}
			dataTestId={`pbj-staffing-edit-${field}-${id}`}
		/>
	);
};
